import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";

export interface RelatorioItem {
  id: number; 
  franquia_id?: number;
  franqueado: string;
  cliente: string;
  plano: string;
  valorPago: number;
  dataPagamento?: string | null;
  formaPagamento?: string | null;
  status?: "Pago" | "Pendente" | null;
}

interface Props {
  dados: RelatorioItem[];
  tipoUsuario: "ADMIN" | "FRANQUIA";
  franqueadoLogado?: string;
  viewMode: "RESUMO_FRANQUIAS" | "DETALHE_FRANQUIA";
  franquiaSelecionada: string | null;
  onSelectFranquia?: (
    franquiaId: number,
    franquiaNome: string
  ) => void;

  onSalvarEdicao?: (
    id: number,
    payload: {
      status: "Pago" | "Pendente";
      forma_pagamento: string;
    }
  ) => Promise<void>;
}


interface Option {
  value: string;
  label: string;
}

const RelatorioFinanceiroTable: React.FC<Props> = ({
  dados,
  tipoUsuario,
  viewMode,
  franquiaSelecionada,
  onSelectFranquia,
  onSalvarEdicao
}) => {
  /* =======================
     ESTADOS LOCAIS
  ======================= */
  const [dadosTabela, setDadosTabela] = useState<RelatorioItem[]>([]);

  const [franqueado, setFranqueado] = useState<Option | null>(null);
  const [plano, setPlano] = useState<Option | null>(null);
  const [status, setStatus] = useState<Option | null>(null);

  const [linhaEditando, setLinhaEditando] = useState<number | null>(null);
  const [formaEdit, setFormaEdit] = useState("");
  const [statusEdit, setStatusEdit] =
    useState<"Pago" | "Pendente">("Pendente")

  /* =======================
     SINCRONIZA DADOS
  ======================= */
  useEffect(() => {
    setDadosTabela(dados);
  }, [dados]);

  /* =======================
     FILTROS
  ======================= */
  const franqueadoOptions = useMemo<Option[]>(() => {
    return Array.from(
      new Set(dadosTabela.map(d => d.franqueado))
    ).map(f => ({ value: f, label: f }));
  }, [dadosTabela]);

  const planoOptions = useMemo<Option[]>(() => {
    return Array.from(
      new Set(dadosTabela.map(d => d.plano))
    ).map(p => ({ value: p, label: p }));
  }, [dadosTabela]);

  const statusOptions: Option[] = [
    { value: "Pago", label: "Pago" },
    { value: "Pendente", label: "Pendente" },
  ];

  /* =======================
     DADOS FILTRADOS
  ======================= */
  const dadosFiltrados = useMemo(() => {
    return dadosTabela.filter(item =>
      (!franqueado || item.franqueado === franqueado.value) &&
      (!plano || item.plano === plano.value) &&
      (!status || item.status === status.value)
    );
  }, [dadosTabela, franqueado, plano, status]);


  /* =======================
     RESUMO POR FRANQUIA
  ======================= */
  const resumoPorFranquia = useMemo(() => {
    if (viewMode !== "RESUMO_FRANQUIAS") return [];

    const map = new Map<number, RelatorioItem & { total: number }>();

    dadosTabela.forEach(item => {
      if (!item.franquia_id) return;

      const atual = map.get(item.franquia_id);

      if (!atual) {
        map.set(item.franquia_id, {
          ...item,
          total: item.valorPago,
        });
      } else {
        atual.total += item.valorPago;
      }
    });

    return Array.from(map.values());
  }, [dadosTabela, viewMode]);

  useEffect(() => {
    setDadosTabela(dados);
    setLinhaEditando(null);
  }, [dados]);

  /* =======================
     RENDER
  ======================= */
  return (
    <div className="space-y-6">
      {tipoUsuario === "ADMIN" &&
        viewMode === "DETALHE_FRANQUIA" && (
          <p className="text-sm text-gray-500">
            Exibindo assinantes da franquia selecionada
          </p>
        )}

      {/* TABELA */}
      <div className="bg-white rounded-lg shadow overflow-x-auto">
        <table className="w-full text-sm text-center">
          <thead className="bg-gray-100">
            <tr>
              <th className="p-3">Franqueado</th>
              {viewMode === "DETALHE_FRANQUIA" && (
                <>
                  <th className="p-3">Assinante</th>
                  <th className="p-3">Plano</th>
                </>
              )}
              <th className="p-3">Valor</th>
              <th className="p-3">Data</th>
              <th className="p-3">Forma</th>
              <th className="p-3">Status</th>
                {tipoUsuario === "ADMIN" && (
                  <th className="p-3 text-center">Ações</th>
                )}
            </tr>
          </thead>

          <tbody>
            {viewMode === "RESUMO_FRANQUIAS" &&
              resumoPorFranquia.map(item => (
                <tr key={item.id} className="border-t">
                  <td className="p-3">
                    <button
                      className="text-blue-600 hover:underline"
                      onClick={() =>
                        onSelectFranquia?.(
                          item.franquia_id!,
                          item.franqueado
                        )
                      }
                    >
                      {item.franqueado}
                    </button>
                  </td>

                  <td className="p-3 font-semibold">
                    R$ {item.total.toFixed(2)}
                  </td>

                  <td className="p-3">
                    {item.dataPagamento
                      ? new Date(item.dataPagamento).toLocaleDateString("pt-BR")
                      : "—"}
                  </td>

                  {/* FORMA */}
                  <td className="p-3">
                    {linhaEditando === item.id ? (
                      <select
                        value={formaEdit}
                        onChange={e => setFormaEdit(e.target.value)}
                        className="border rounded px-2 py-1"
                      >
                        <option value="">Selecione</option>
                        <option value="Pix">Pix</option>
                        <option value="Cartão">Cartão</option>
                        <option value="Boleto">Boleto</option>
                      </select>
                    ) : (
                      item.formaPagamento ?? "—"
                    )}
                  </td>

                  {/* STATUS */}
                  <td className="p-3">
                    {linhaEditando === item.id ? (
                      <select
                        value={statusEdit}
                        onChange={e =>
                          setStatusEdit(e.target.value as "Pago" | "Pendente")
                        }
                        className="border rounded px-2 py-1"
                      >
                        <option value="Pago">Pago</option>
                        <option value="Pendente">Pendente</option>
                      </select>
                    ) : (
                      item.status ?? "—"
                    )}
                  </td>

                  {/* AÇÕES */}
                  {tipoUsuario === "ADMIN" && (
                    <td className="p-3 text-center">
                      {linhaEditando === item.id ? (
                        <>
                          <button
                            className="text-green-600 mr-2"
                            onClick={() =>
                              onSalvarEdicao?.(item.id, {
                                status: statusEdit,
                                forma_pagamento: formaEdit,
                              })
                            }
                          >
                            ✔
                          </button>

                          <button
                            className="text-red-600"
                            onClick={() => setLinhaEditando(null)}
                          >
                            ✖
                          </button>
                        </>
                      ) : (
                        <button
                          className="text-blue-600"
                          onClick={() => {
                            setLinhaEditando(item.id);
                            setFormaEdit(item.formaPagamento ?? "");
                            setStatusEdit(item.status ?? "Pendente");
                          }}
                        >
                          ✏️
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}


            {viewMode === "DETALHE_FRANQUIA" &&
              dadosFiltrados.map((item) => (
                <tr key={item.id} className="border-t">
                  <td className="p-3">{item.franqueado}</td>
                  <td className="p-3">{item.cliente}</td>
                  <td className="p-3">{item.plano}</td>
                  <td className="p-3">
                    R$ {item.valorPago.toFixed(2)}
                  </td>
                  <td className="p-3">
                    {item.dataPagamento ?? "—"}
                  </td>
                  <td className="p-3">
                    {linhaEditando === item.id ? (
                      <select
                        value={formaEdit}
                        onChange={e => setFormaEdit(e.target.value)}
                        className="border rounded px-2 py-1"
                      >
                        <option value="">Selecione</option>
                        <option value="Pix">Pix</option>
                        <option value="Cartão">Cartão</option>
                        <option value="Boleto">Boleto</option>
                      </select>
                    ) : (
                      item.formaPagamento ?? "—"
                    )}
                  </td>

                  <td className="p-3">
                    {linhaEditando === item.id ? (
                      <select
                        value={statusEdit}
                        onChange={e =>
                          setStatusEdit(e.target.value as "Pago" | "Pendente")
                        }
                        className="border rounded px-2 py-1"
                      >
                        <option value="Pago">Pago</option>
                        <option value="Pendente">Pendente</option>
                      </select>
                    ) : (
                      item.status ?? "—"
                    )}
                  </td>

                    <td className="p-3 text-center">
                      {linhaEditando === item.id ? (
                        <>
                          <button
                            className="text-green-600 mr-2"
                            onClick={async () => {
                              await onSalvarEdicao?.(item.id, {
                                status: statusEdit,
                                forma_pagamento: formaEdit,
                              });

                              setLinhaEditando(null);
                            }}
                          >
                            ✔
                          </button>

                          <button
                            className="text-red-600"
                            onClick={() => setLinhaEditando(null)}
                          >
                            ✖
                          </button>
                        </>
                      ) : (
                        <button
                          className="text-blue-600"
                          onClick={() => {
                            setLinhaEditando(item.id);
                            setFormaEdit(item.formaPagamento ?? "");
                            setStatusEdit(item.status ?? "Pendente");
                          }}
                        >
                          ✏️
                        </button>
                      )}
                    </td>


                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default RelatorioFinanceiroTable;
